import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useStudioTheme } from '../hooks/useStudioTheme';
import { getAIResponse, getAIResponseFromImage } from '../services/openaiService';
import Header from './Header';
import './InterviewPage.css';
// Light/gradient surface for the standalone route. Must come after the base
// stylesheet so equal-specificity rules resolve in its favour.
import './InterviewPage.theme.css';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import ProductSidebar from './ProductSidebar';
import { installNavigationGuard } from '../utils/navigationGuard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { createDoc, PDF, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawMetaCard, drawSectionHeading } from '../utils/pdfBrand';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Add pulse animation styles
const styles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
  }
`;

// Inject styles into document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-pulse-animation]')) {
    styleSheet.setAttribute('data-pulse-animation', 'true');
    document.head.appendChild(styleSheet);
  }
}

const CodeBlock = ({ language, value }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div className="terview-code-block">
      <div className="terview-code-header">
        <span className="terview-code-lang">{language || 'code'}</span>
        <button
          type="button"
          className="terview-code-copy"
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          background: '#0d1117',
          fontSize: '0.88em',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } }}
      >
        {value.replace(/\n$/, '')}
      </SyntaxHighlighter>
    </div>
  );
};

// ── Settings primitives shared with the Electron pane ────────────────
const IP_SETTINGS_KEY = 'terview.interview.settings.v1';
const IP_DEFAULT_SETTINGS = {
  responseLength: 'default',      // 'concise' | 'default' | 'detailed'
  interviewLanguage: 'en',        // ISO code or 'auto'
  aiResponseLanguage: 'auto',     // 'auto' or language name
  processTier: 'medium',          // 'fast' | 'medium' | 'quality'
  autoSkipNoise: true,
  noiseThreshold: 0.22,           // 0.05 – 0.6
  fontSize: 15,                   // 12 – 22 px
  hideQuestionText: false,
  autoCopyResponse: false,
};
const IP_LANGUAGES = [
  { code: 'auto', label: 'Auto detect', name: 'Auto' },
  { code: 'en',   label: 'English',     name: 'English' },
  { code: 'es',   label: 'Spanish',     name: 'Spanish' },
  { code: 'fr',   label: 'French',      name: 'French' },
  { code: 'de',   label: 'German',      name: 'German' },
  { code: 'it',   label: 'Italian',     name: 'Italian' },
  { code: 'pt',   label: 'Portuguese',  name: 'Portuguese' },
  { code: 'hi',   label: 'Hindi',       name: 'Hindi' },
  { code: 'zh',   label: 'Chinese',     name: 'Chinese' },
  { code: 'ja',   label: 'Japanese',    name: 'Japanese' },
  { code: 'ko',   label: 'Korean',      name: 'Korean' },
  { code: 'ar',   label: 'Arabic',      name: 'Arabic' },
  { code: 'ru',   label: 'Russian',     name: 'Russian' },
  { code: 'nl',   label: 'Dutch',       name: 'Dutch' },
];

function IPField({ label, hint, children }) {
  return (
    <div className="ip-setting">
      <div className="ip-setting-label">{label}</div>
      {hint && <div className="ip-setting-hint">{hint}</div>}
      <div className="ip-setting-control">{children}</div>
    </div>
  );
}
function IPSegmented({ value, onChange, options }) {
  return (
    <div className="ip-segmented" role="group">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`ip-segment ${value === opt.value ? 'active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
function IPToggleRow({ label, checked, onChange }) {
  return (
    <label className="ip-toggle-row">
      <span className="ip-toggle-text">{label}</span>
      <span
        className={`ip-toggle ${checked ? 'on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onChange(!checked);
          }
        }}
      >
        <span className="ip-toggle-knob" />
      </span>
    </label>
  );
}

const markdownComponents = {
  code({ inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const value = String(children ?? '');
    if (!inline && (match || value.includes('\n'))) {
      return <CodeBlock language={match ? match[1] : ''} value={value} />;
    }
    return <code className="terview-inline-code" {...props}>{children}</code>;
  },
  // Render the rest of the response as plain prose (outside the code box).
  p: ({ children }) => <p className="terview-md-p">{children}</p>,
  ul: ({ children }) => <ul className="terview-md-ul">{children}</ul>,
  ol: ({ children }) => <ol className="terview-md-ol">{children}</ol>,
  li: ({ children }) => <li className="terview-md-li">{children}</li>,
  h1: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
  h2: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
  h3: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
};

const InterviewPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user, loading, logout, credits } = useUser();
  // Also mirrors the class onto <body>, which is what puts the portalled
  // dialogs inside a `.cls-light` scope.
  const themeClass = useStudioTheme(!embedded);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  const isElectronApp = window.navigator.userAgent.toLowerCase().includes('electron');
  const isWindows = navigator.userAgent.toLowerCase().includes('windows');
  const loopbackDeviceName = isWindows ? 'VB-Cable' : 'BlackHole';

  const [showJobForm, setShowJobForm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // When the user clicks a launchpad tile or header link mid-session, we
  // stash the requested target here and pop the exit-confirm modal. The
  // modal's "Yes" handler then runs cleanup and follows through with the
  // navigation. Null = the modal was triggered by the browser back/forward
  // popstate guard, which has its own fallback (dashboard).
  const [pendingExit, setPendingExit] = useState(null);
  // True while we're auto-starting the interview from a navigation
  // prefill (i.e. user filled the form on /InterviewAssistant and was
  // bounced here). Suppresses the marketing hero + form flash so the
  // user goes from "loading" straight into the live chat.
  const bootingFromPrefillRef = useRef(false);
  const [bootingFromPrefill, setBootingFromPrefill] = useState(false);
  // Close the job form modal on Escape, mirroring the click-outside behavior.
  useEffect(() => {
    if (!showJobForm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setShowJobForm(false); setIsEditing(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showJobForm]);
  const [formData, setFormData] = useState({
    jobRole: '',
    experienceLevel: '',
    company: '',
    jobDescription: '',
    resumeText: ''
  });
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [uploadedJobDescFile, setUploadedJobDescFile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  // ── Pre-fill from desktop pane ───────────────────────────────────────────
  // The Electron InterviewAssistantPane collects role / company / level / JD
  // / resume in its own clean form and navigates here with `state.prefill`.
  // We do three things on a prefill arrival:
  //   1. Mount-time merge of the form fields.
  //   2. When profileData loads, build resumeText from it for 'profile' choice.
  //   3. Once every required field is populated, auto-trigger Start Interview
  //      so the user goes straight from Pane → live interview without a
  //      duplicate "Start" click.
  const location = useLocation();
  const prefillRef = useRef(location.state?.prefill || null);
  const autoStartedRef = useRef(false);
  // Holds the latest `handleStartInterview` once it's defined further
  // below. The auto-start useEffect (hoisted to live alongside the other
  // top-level hooks so the Rules of Hooks aren't violated by the early
  // `if (loading) return / if (!user) return null` branches) reads through
  // this ref to fire the start without forward-referencing the function.
  const handleStartInterviewRef = useRef(null);
  useEffect(() => {
    const pf = prefillRef.current;
    if (!pf) return;
    // Mark "booting" the moment we know a prefill is in flight, so the
    // first paint of /interview-session hides the marketing chrome
    // instead of flashing the form briefly before auto-start kicks in.
    bootingFromPrefillRef.current = true;
    setBootingFromPrefill(true);
    setFormData(prev => ({
      ...prev,
      jobRole: pf.jobRole || prev.jobRole,
      company: pf.company || prev.company,
      experienceLevel: pf.experienceLevel || prev.experienceLevel,
      jobDescription: pf.jobDescription || prev.jobDescription,
      resumeText: pf.resumeText || prev.resumeText,
    }));
    if (pf.resumeChoice === 'profile' || pf.resumeChoice === 'upload') {
      setResumeChoice(pf.resumeChoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 2: When profileData arrives later, fill resumeText for 'profile' choice.
  useEffect(() => {
    const pf = prefillRef.current;
    if (!pf || pf.resumeChoice !== 'profile' || !profileData) return;
    setFormData(prev => prev.resumeText
      ? prev
      : { ...prev, resumeText: buildResumeTextFromProfile(profileData) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareError, setScreenShareError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(30 * 60);
  const sessionDuration = (credits >= 999000) ? 90 * 60 : 30 * 60;
  const [interviewSessionId, setInterviewSessionId] = useState(null);
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [isAutoRecording, setIsAutoRecording] = useState(false);
  const [autoRecordingError, setAutoRecordingError] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const conversationContainerRef = useRef(null);
  const prevConvLengthRef = useRef(0);
  const latestQuestionRef = useRef(null);
  const userScrolledAwayRef = useRef(false);
  const lastProgrammaticScrollRef = useRef(0);
  const screenShareRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const autoRecognitionRef = useRef(null);
  const autoAudioContextRef = useRef(null);
  const autoStreamRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const restartAttemptsRef = useRef(0);
  const maxRestartAttempts = 3;
  const isProcessingRef = useRef(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAlwaysOnTop, setIsAlwaysOnTop] = useState(true);
  let conversationIdCounter = useRef(0);

  const [micActive, setMicActive] = useState(false);
  // Typed-question composer at the bottom of the chat (parity with the
  // Electron pane). The user can paste/type a question and click Get
  // answer — handler routes through the existing generateAIResponse.
  const [questionInput, setQuestionInput] = useState('');
  // Per-message UX state: which AI response is currently being copied,
  // so the Copy button can flip to "✓ Copied" briefly.
  const [copiedId, setCopiedId] = useState(null);

  const micRecorderRef = useRef(null);
  const micChunksRef = useRef([]);
  // Lazy refs to stopAutoRecording / stopScreenShare so the unmount
  // cleanup effect (declared further below, but BEFORE both functions)
  // can call them without triggering a Temporal Dead Zone error under
  // React 18 StrictMode's double-invoke. Each ref is populated with
  // the real implementation right after its function definition.
  const stopAutoRecordingFnRef = useRef(() => {});
  const stopScreenShareFnRef = useRef(() => {});
  // Holds the latest screenshot handlers so the capture effect (which
  // has to live above the auth early-returns to satisfy Rules of Hooks)
  // can call the current closures without re-binding.
  // - screenshotHandlerRef → handleScreenshotQuestion (web keydown)
  // - sendScreenshotToAIRef → sendScreenshotToAI (Electron IPC image)
  const screenshotHandlerRef = useRef(() => {});
  const sendScreenshotToAIRef = useRef(() => {});


  // ── Settings (mirrors the Electron InterviewAssistantPane) ─────────
  // Persisted to the same localStorage key so a user who configures
  // settings in the desktop app sees them honored in the browser too.
  const [settings, setSettings] = useState(IP_DEFAULT_SETTINGS);
  const [draftSettings, setDraftSettings] = useState(IP_DEFAULT_SETTINGS);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(IP_SETTINGS_KEY);
      if (raw) {
        const parsed = { ...IP_DEFAULT_SETTINGS, ...JSON.parse(raw) };
        setSettings(parsed);
        setDraftSettings(parsed);
      }
    } catch { /* ignore corrupt cache */ }
  }, []);

  // Screenshot capture wiring. Two distinct paths:
  //
  //  WEB  — ⌘/Ctrl+Shift+S keydown (only when the Terview tab is
  //         focused) grabs a frame from the active screen-share.
  //
  //  ELECTRON — the OS-level globalShortcut in the main process does
  //         a native desktopCapturer screen grab and sends the image
  //         back over 'terview:screenshot-captured'. We do NOT attach
  //         the keydown listener in Electron, otherwise focusing the
  //         Terview window would double-fire (keydown + globalShortcut).
  //
  // Hooks live above the auth early-returns to satisfy Rules of Hooks;
  // handlers are dereferenced through refs kept in sync further down.
  useEffect(() => {
    const ipc = typeof window !== 'undefined' && window.electron && window.electron.ipcRenderer;
    const isElectron = !!(ipc && typeof ipc.on === 'function');

    let onKeyDown = null;
    if (!isElectron) {
      onKeyDown = (e) => {
        const meta = e.metaKey || e.ctrlKey;
        if (meta && e.shiftKey && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          screenshotHandlerRef.current?.();
        }
      };
      window.addEventListener('keydown', onKeyDown);
    }

    if (isElectron) {
      // Native capture finished — main process sends the image data URL.
      ipc.on('terview:screenshot-captured', (_event, dataUrl) => {
        console.log('[screenshot] image received from main, length:', dataUrl?.length);
        sendScreenshotToAIRef.current?.(dataUrl);
      });
      // Generic capture failure (rare — most issues land in permission-denied).
      ipc.on('terview:screenshot-error', (_event, msg) => {
        console.warn('[screenshot] capture error:', msg);
        alert(`Screenshot capture failed: ${msg || 'unknown error'}`);
      });
      // macOS Screen Recording permission missing — main has already
      // opened System Settings. Just tell the user what to do next.
      ipc.on('terview:screenshot-permission-denied', () => {
        alert(
          'Terview needs Screen Recording permission to capture your screen.\n\n' +
          'System Settings just opened — enable Terview under ' +
          '"Privacy & Security → Screen Recording", then restart the app and try again.'
        );
      });
    }

    return () => {
      if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
      if (isElectron && typeof ipc.removeAllListeners === 'function') {
        ipc.removeAllListeners('terview:screenshot-captured');
        ipc.removeAllListeners('terview:screenshot-error');
        ipc.removeAllListeners('terview:screenshot-permission-denied');
      }
    };
  }, []);

  // Font size is now driven by the settings panel; keep a derived local so
  // existing JSX referencing `fontSize` keeps working.
  const fontSize = settings.fontSize;
  const setFontSize = (next) => {
    setSettings(s => {
      const updated = { ...s, fontSize: next };
      try { localStorage.setItem(IP_SETTINGS_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };


  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  // Function to extract text from PDF
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF contains readable text.');
    }
  };

  // Function to extract text from DOCX
  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try pasting the text manually.');
    }
  };

  // Function to extract text from DOC
  const extractTextFromDOC = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOC:', error);
      throw new Error('Failed to parse DOC file. This format may not be fully supported. Please try converting to DOCX or pasting the text manually.');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingFile(true);
    setFileUploadError('');

    try {
      if (type === 'resume') {
        setUploadedResumeFile(file);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(file);
      }

      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(file);
        });
      } else if (
        file.type === 'application/pdf' || 
        file.type === 'application/x-pdf' ||
        file.type === 'binary/octet-stream' ||
        file.name.toLowerCase().endsWith('.pdf')
      ) {
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/octet-stream' ||
        file.name.toLowerCase().endsWith('.docx')
      ) {
        extractedText = await extractTextFromDOCX(file);
      } else if (
        file.type === 'application/msword' || 
        file.name.toLowerCase().endsWith('.doc')
      ) {
        extractedText = await extractTextFromDOC(file);
      } else {
        throw new Error('Unsupported file format. Please upload .txt, .pdf, .doc, or .docx files.');
      }

      extractedText = extractedText.trim();

      if (!extractedText) {
        throw new Error('No text could be extracted from the file. Please ensure the file contains readable text.');
      }

      if (type === 'resume') {
        setFormData(prevData => ({
          ...prevData,
          resumeText: extractedText
        }));
        setFileUploadError('✓ Resume uploaded successfully!');
      } else if (type === 'jobDesc') {
        setFormData(prevData => ({
          ...prevData,
          jobDescription: extractedText
        }));
        setFileUploadError('✓ Job description uploaded successfully!');
      }

      setTimeout(() => {
        setFileUploadError('');
      }, 3000);

    } catch (error) {
      console.error('Error processing file:', error);
      setFileUploadError(error.message || 'Failed to process file. Please try pasting the text manually.');
      
      if (type === 'resume') {
        setUploadedResumeFile(null);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(null);
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const stopAutoRecording = () => {
    const hasAnythingToStop = !!(
      autoRecognitionRef.current?.mediaRecorder ||
      autoStreamRef.current ||
      autoAudioContextRef.current ||
      audioProcessorRef.current ||
      silenceTimeoutRef.current
    );
    if (!hasAnythingToStop) return;

    console.log('🛑 Stopping auto recording...');

    if (autoRecognitionRef.current?.mediaRecorder) {
      try {
        if (autoRecognitionRef.current.mediaRecorder.state !== 'inactive') {
          autoRecognitionRef.current.mediaRecorder.stop();
        }
      } catch (error) {
        console.warn('Error stopping media recorder:', error);
      }
    }

    if (autoStreamRef.current) {
      autoStreamRef.current.getTracks().forEach(track => track.stop());
      autoStreamRef.current = null;
    }

    if (autoAudioContextRef.current) {
      try {
        autoAudioContextRef.current.close();
      } catch (error) {
        console.warn('Error closing audio context:', error);
      }
      autoAudioContextRef.current = null;
    }

    if (audioProcessorRef.current) {
      try {
        audioProcessorRef.current.disconnect();
      } catch (error) {
        console.warn('Error disconnecting audio processor:', error);
      }
      audioProcessorRef.current = null;
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    autoRecognitionRef.current = null;
    restartAttemptsRef.current = 0;
    setIsAutoRecording(false);
    console.log('✅ Auto recording stopped');
  };
  // Expose stopAutoRecording to the unmount-cleanup effect via a ref —
  // the effect lives above this declaration and would TDZ on direct
  // reference under React 18 StrictMode.
  stopAutoRecordingFnRef.current = stopAutoRecording;

  useEffect(() => {
    // When embedded inside InterviewAssistantLanding, the parent owns the un-auth UX —
    // don't redirect or we'll loop (landing → embed → navigate('/') → landing again).
    if (embedded) return;
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate, embedded]);

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  // Detect manual scroll INPUTS (wheel, touch, keyboard) directly, instead
  // of trying to infer it from the resulting scroll event — which fires
  // async and often loses the race against the next streaming pin().
  // The moment the user signals intent to scroll, pinning pauses until
  // the next new message arrives.
  //
  // Depends on `interviewStarted` because .conversation-container is
  // conditionally rendered — the ref is null until the interview begins,
  // and without this dep the listeners would never attach.
  useEffect(() => {
    if (!interviewStarted) return;
    const containerEl = conversationContainerRef.current;
    if (!containerEl) return;
    const markUserScrolled = () => { userScrolledAwayRef.current = true; };
    const onKey = (e) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) {
        userScrolledAwayRef.current = true;
      }
    };
    containerEl.addEventListener('wheel',      markUserScrolled, { passive: true });
    containerEl.addEventListener('touchstart', markUserScrolled, { passive: true });
    containerEl.addEventListener('mousedown',  markUserScrolled, { passive: true });
    containerEl.addEventListener('keydown',    onKey);
    return () => {
      containerEl.removeEventListener('wheel',      markUserScrolled);
      containerEl.removeEventListener('touchstart', markUserScrolled);
      containerEl.removeEventListener('mousedown',  markUserScrolled);
      containerEl.removeEventListener('keydown',    onKey);
    };
  }, [interviewStarted]);

  useEffect(() => {
    // Re-anchor on EVERY conversation update — including streaming text
    // changes — so the question stays glued to the top of the viewport
    // regardless of how much the answer grows below it. UNLESS the user has
    // scrolled away, in which case we leave them alone until the next new
    // message resets the take-over flag.
    const containerEl = conversationContainerRef.current;
    if (!containerEl) return;

    const pin = () => {
      // Detect direction by reading the live computed style — base rule
      // is column-reverse (embedded studio), live mode overrides to
      // column. The two need opposite scrollTop semantics.
      const isColumnReverse = window.getComputedStyle(containerEl).flexDirection === 'column-reverse';

      const qEl = latestQuestionRef.current;
      if (!qEl) {
        // No latest question yet — slam the scroll to the "newest"
        // edge. In column-reverse newest sits at scrollTop = 0; in
        // normal column newest sits at scrollTop = scrollHeight.
        containerEl.scrollTop = isColumnReverse ? 0 : containerEl.scrollHeight;
        lastProgrammaticScrollRef.current = containerEl.scrollTop;
        return;
      }
      if (userScrolledAwayRef.current) return; // user took over — don't fight
      const containerRect = containerEl.getBoundingClientRect();
      const qRect = qEl.getBoundingClientRect();
      const delta = qRect.top - containerRect.top;
      if (Math.abs(delta) > 1) {
        containerEl.scrollTop += delta;
        lastProgrammaticScrollRef.current = containerEl.scrollTop;
      }
    };

    if (conversation.length !== prevConvLengthRef.current) {
      // New message just inserted — reset the user-took-over flag and pin.
      // Wait two frames so the DOM has fully committed before measuring.
      prevConvLengthRef.current = conversation.length;
      userScrolledAwayRef.current = false;
      requestAnimationFrame(() => requestAnimationFrame(pin));
    } else {
      // Same-length streaming update — pin immediately to counteract any
      // layout shift caused by the answer growing.
      pin();
    }
  }, [conversation]);

  useEffect(() => {
    return () => {
      // Belt-and-suspenders: if the component unmounts via any path
      // we didn't explicitly cover (router pop, parent re-render, hot
      // reload), release the screen-capture stream and recorders so
      // Chrome's "Sharing this tab" bar / mic indicator don't outlive
      // the page. Refs are dereferenced lazily, so they're safe even
      // though the underlying functions are declared later in the body.
      stopAutoRecordingFnRef.current?.();
      stopScreenShareFnRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (interviewStarted && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleEndInterview(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [interviewStarted, timeRemaining]);

  useEffect(() => {
    if (interviewStarted && isAutoMode) {
      startAutoRecording();
    } else {
      stopAutoRecording();
    }
  }, [interviewStarted, isAutoMode]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (interviewStarted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [interviewStarted]);

  useEffect(() => {
    if (isElectronApp) {
      document.body.classList.add('electron-app');
    }
    return () => {
      document.body.classList.remove('electron-app');
    };
  }, [isElectronApp]);

  // Lock body scroll during a live session so Chrome's screen-sharing
  // notification bar can't push the page taller than the viewport (which
  // would expose a vertical scrollbar on the right). The chat panel handles
  // its own internal scroll, so suppressing the outer scroll is safe.
  useEffect(() => {
    if (interviewStarted && !embedded) {
      document.body.classList.add('ip-live-lock');
    } else {
      document.body.classList.remove('ip-live-lock');
    }
    return () => {
      document.body.classList.remove('ip-live-lock');
    };
  }, [interviewStarted, embedded]);

  // Browser back/forward guard. While the interview is live, intercept
  // popstate and prompt the user before letting them navigate away — a
  // mis-clicked back button used to silently end the session and a
  // forward click would re-mount this page and restart the interview.
  // We push a sentinel history entry on mount and re-push it on every
  // popstate so the user stays pinned until they confirm exit.
  useEffect(() => {
    if (!interviewStarted || embedded) return;
    window.history.pushState({ ipSentinel: true }, '');
    const handlePop = () => {
      window.history.pushState({ ipSentinel: true }, '');
      setPendingExit(null); // popstate has no path; modal "Yes" falls back to dashboard
      setShowExitConfirm(true);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [interviewStarted, embedded]);

  // In-app navigation guard. Launchpad tiles, header links, brand click —
  // anything routed through utils/navigationGuard.requestNavigation hits
  // this handler instead of navigating directly. We capture the target
  // path + the caller's navigate fn, then pop the same exit-confirm modal.
  useEffect(() => {
    if (!interviewStarted || embedded) return;
    return installNavigationGuard((path, navigateFn) => {
      setPendingExit({ path, navigateFn });
      setShowExitConfirm(true);
    });
  }, [interviewStarted, embedded]);

  // Auto-start trigger for prefill flow — fires once per mount when every
  // required field is populated. Skips the manual two-step ("Enter Job
  // Information" → "Start Interview") for users coming from the desktop
  // AI Interview Assistant pane. Lives here, before the conditional
  // returns below, so the hook count stays stable across renders when
  // `loading` flips false or `user` resolves from null.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!prefillRef.current) return;
    if (interviewStarted) return;
    if (!formData.jobRole || !formData.experienceLevel || !formData.jobDescription || !formData.resumeText) return;
    autoStartedRef.current = true;
    handleStartInterviewRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, interviewStarted]);

  // Direct-visit redirect: if the user lands on /interview-session by
  // refresh or bookmark (no prefill in flight, no active session, not
  // embedded), bounce them to the Interview Assistant landing page so
  // they get the full setup flow instead of the bare "Enter Job
  // Information" card. Runs once on mount.
  useEffect(() => {
    if (embedded) return;
    if (prefillRef.current) return; // arrived via landing-page studio handoff
    if (interviewStarted) return;   // already in a live session somehow
    navigate('/InterviewAssistant', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The theme class is mirrored onto <body> by useStudioTheme (called at the
  // top of this component) so the portalled dialogs — settings, exit-confirm
  // and the job form all render with createPortal(..., document.body) — sit
  // inside a `.cls-light` scope.
  //
  // This used to be a hand-rolled copy of that effect, from before the hook
  // existed, and its cleanup removed the class unconditionally. This page
  // redirects to /InterviewAssistant when there is no live session, so it
  // mounted and immediately unmounted — taking `cls-light` off <body> with
  // it and leaving every portalled dialog dark on a light page.

  // Clear the booting splash once the interview is live — the live
  // chat takes over the page, so we no longer need the loading mask.
  useEffect(() => {
    if (interviewStarted && bootingFromPrefill) {
      bootingFromPrefillRef.current = false;
      setBootingFromPrefill(false);
    }
  }, [interviewStarted, bootingFromPrefill]);

  // Clear the booting splash if auto-start fails (e.g. insufficient
  // credits). Falls back to the normal form so the user can recover.
  useEffect(() => {
    if (showInsufficientCreditsModal && bootingFromPrefill) {
      bootingFromPrefillRef.current = false;
      setBootingFromPrefill(false);
    }
  }, [showInsufficientCreditsModal, bootingFromPrefill]);

  // Safety net: if auto-start hasn't fired after 6s (e.g. profile data
  // never arrives, resume parse stalled), drop the splash and show the
  // form so the user isn't stuck on a spinner forever.
  useEffect(() => {
    if (!bootingFromPrefill) return;
    const t = setTimeout(() => {
      bootingFromPrefillRef.current = false;
      setBootingFromPrefill(false);
    }, 6000);
    return () => clearTimeout(t);
  }, [bootingFromPrefill]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNavigateToHome = () => {
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleHeaderNavigation = async (path) => {
    if (interviewStarted) {
      // Don't show confirmation for logout-redirect (already confirmed in Header)
      if (path === '/logout-redirect') {
        try {
          // Save the interview first
          if (conversation.length > 0) {
            await saveInterviewSession(false);
            console.log('✅ Interview session saved successfully');
          }
          
          // Reset interview state
          setInterviewStarted(false);
          stopAutoRecording();
          stopScreenShare();
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }

          // Call backend logout API first
          try {
            const response = await fetch(`${API_BASE_URL}/api/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ email: user?.email })
            });

            const data = await response.json();
            
            if (response.ok) {
              console.log('Logout successful:', data);
            } else {
              console.log('Logout API error, but proceeding with frontend logout:', data.message);
            }
          } catch (error) {
            console.error('Logout API error, but proceeding with frontend logout:', error);
          }
          
          // Then trigger frontend logout
          logout();
          navigate('/');
        } catch (error) {
          console.error('Error ending interview:', error);
          alert('Failed to save interview session.');
        }
        return;
      }

      // For other navigation, route through the same exit-confirm modal
      // the launchpad sidebar uses. The modal's "Yes" handler runs the
      // save + cleanup and then calls our navigate fn with the target.
      // We return a Promise so Header callers that chain `.then()` (e.g.
      // section-scroll after nav) don't break.
      return new Promise((resolve) => {
        setPendingExit({
          path,
          navigateFn: (target) => navigate(target),
          resolve,
        });
        setShowExitConfirm(true);
      });
    } else {
      // Handle special logout redirect when no interview
      if (path === '/logout-redirect') {
        // Call backend logout API
        try {
          const response = await fetch(`${API_BASE_URL}/api/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ email: user?.email })
          });

          const data = await response.json();
          
          if (response.ok) {
            console.log('Logout successful:', data);
          } else {
            console.log('Logout API error, but proceeding with frontend logout:', data.message);
          }
        } catch (error) {
          console.error('Logout API error, but proceeding with frontend logout:', error);
        }
        
        logout();
        navigate('/');
      } else {
        navigate(path);
      }
    }
  };

  if (loading) {
    return (
      <div className="neural-glass-homepage">
        <div className="neural-background"></div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#fff',
          fontSize: '1.2rem'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/deduct-credit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to deduct credit');
      }

      const data = await response.json();
      return data.remainingCredits;
    } catch (error) {
      console.error('Error deducting credit:', error);
      throw error;
    }
  };

  const handleStartInterview = async () => {
  if (formData.jobRole && formData.experienceLevel && formData.jobDescription && formData.resumeText) {
    // EDIT MODE: the modal is being used to update job info during a live
    // session. Don't deduct a credit again, don't reset the timer — just
    // close the form and keep the existing interview state.
    if (isEditing) {
      setShowJobForm(false);
      setIsEditing(false);
      return;
    }

    // EMBEDDED MODE: when this InterviewPage is mounted inside the
    // landing page studio (e.g. /InterviewAssistant), don't run the
    // interview inline — hand off to the dedicated /interview-session
    // page so the user gets a focused full-screen experience instead of
    // the chat appearing in the middle of marketing content.
    // Credit deduction + session start happen on the destination page
    // via the existing prefill auto-start mechanism, which deducts
    // exactly once and shows the modal on failure.
    if (embedded) {
      setShowJobForm(false);
      navigate('/interview-session', {
        state: {
          prefill: {
            jobRole: formData.jobRole,
            experienceLevel: formData.experienceLevel,
            company: formData.company,
            jobDescription: formData.jobDescription,
            resumeText: formData.resumeText,
            resumeChoice,
          },
          returnTo: location.pathname,
        },
      });
      return;
    }

    try {
      await deductCredit();
      setInterviewStarted(true);
      setShowJobForm(false);
      setTimeRemaining(sessionDuration);

    } catch (error) {
        if (error.message?.includes('credit') || error.message?.includes('Credit')) {
          setShowJobForm(false); // close job form first so modal is visible
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || 'Failed to start interview. Please check your credits and try again.');
        }
      }
    } else {
      alert('Please complete all required fields before starting the interview.');
    }
  };

  // Keep the auto-start ref pointed at the latest `handleStartInterview`
  // closure so the hoisted top-level useEffect can fire it without
  // forward-referencing the function.
  handleStartInterviewRef.current = handleStartInterview;

  const handleEditJobInfo = () => {
    setIsEditing(true);
    setShowJobForm(true);
  };

  const generateAIResponse = async (interviewerQuestion) => {
    setIsLoading(true);
    setCurrentQuestion(interviewerQuestion);

    // ✅ Add question and placeholder immediately
    const questionId = ++conversationIdCounter.current;
    const aiId = ++conversationIdCounter.current;

    // Newest pair at the FRONT, Q before A within each pair, so the
    // renderer can iterate as-is and column flow puts the latest
    // question at the visual TOP — newest-first reads better than
    // standard-chat for review purposes.
    setConversation(prev => [
      {
        id: questionId,
        sender: 'interviewer',
        message: interviewerQuestion,
        timestamp: new Date().toLocaleTimeString()
      },
      {
        id: aiId,
        sender: 'ai',
        message: '',  // ✅ Start empty, will fill word by word
        timestamp: new Date().toLocaleTimeString(),
        formattedMessage: ''
      },
      ...prev
    ]);

    try {
      const chronologicalConversation = [...conversation].reverse();
      const recentConversation = chronologicalConversation.slice(-4);

      // Translate the AI-language setting from ISO code → human name
      // (the openaiService prompt expects e.g. 'Spanish', not 'es').
      const s = settingsRef.current;
      const langEntry = IP_LANGUAGES.find(l => l.code === s.aiResponseLanguage);
      const aiLangForPrompt = !langEntry || langEntry.code === 'auto' ? 'auto' : langEntry.name;

      const finalText = await getAIResponse(
        interviewerQuestion,
        formData,
        recentConversation,
        (streamedText) => {
          setConversation(prev => prev.map(msg =>
            msg.id === aiId
              ? {
                  ...msg,
                  message: streamedText,
                  formattedMessage: streamedText
                }
              : msg
          ));
        },
        {
          responseLength: s.responseLength,
          aiResponseLanguage: aiLangForPrompt,
          processTier: s.processTier,
        }
      );
      if (s.autoCopyResponse && finalText) {
        try { await navigator.clipboard.writeText(finalText); } catch { /* clipboard unavailable */ }
      }

    } catch (error) {
      console.error('Error generating AI response:', error);

      // ✅ Update placeholder with error message
      setConversation(prev => prev.map(msg =>
        msg.id === aiId
          ? {
              ...msg,
              message: "I apologize, but I'm having trouble generating a response. Could you please try again?",
              formattedMessage: "I apologize, but I'm having trouble generating a response. Could you please try again?"
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
      setCurrentQuestion('');
    }
  };

  const handleAudioTranscript = async (transcript) => {
    if (transcript.trim()) {
      await generateAIResponse(transcript);
    }
  };

  // ── Screenshot → AI ─────────────────────────────────────────────
  // Shared upload + analysis path. Takes a ready image data URL from
  // EITHER capture path (web screen-share frame OR Electron native
  // desktopCapturer) and runs it through the vision model. Drops the
  // screenshot into the chat as the "question" and streams the answer.
  const sendScreenshotToAI = async (dataUrl) => {
    if (!dataUrl) {
      console.warn('[screenshot] sendScreenshotToAI called with no data URL');
      return;
    }
    if (!interviewStarted) {
      console.warn('[screenshot] dropped — interview not started');
      alert('Start the interview first, then press ⌘/Ctrl+Shift+S to capture and ask about your screen.');
      return;
    }
    if (isLoading) {
      console.warn('[screenshot] dropped — already generating a response');
      alert('Wait for the current answer to finish, then capture again.');
      return;
    }
    console.log('[screenshot] processing image, length:', dataUrl.length);

    setIsLoading(true);
    const questionId = ++conversationIdCounter.current;
    const aiId = ++conversationIdCounter.current;

    setConversation(prev => [
      {
        id: questionId,
        sender: 'interviewer',
        message: '',                    // text is empty — image is the question
        image: dataUrl,                 // rendered as an inline thumbnail
        kind: 'screenshot',
        timestamp: new Date().toLocaleTimeString(),
      },
      {
        id: aiId,
        sender: 'ai',
        message: '',
        timestamp: new Date().toLocaleTimeString(),
        formattedMessage: '',
      },
      ...prev
    ]);

    try {
      const recentConversation = [...conversation].reverse().slice(-4);
      const s = settingsRef.current;
      const langEntry = IP_LANGUAGES.find(l => l.code === s.aiResponseLanguage);
      const aiLangForPrompt = !langEntry || langEntry.code === 'auto' ? 'auto' : langEntry.name;

      const finalText = await getAIResponseFromImage(
        dataUrl,
        formData,
        recentConversation,
        (streamedText) => {
          setConversation(prev => prev.map(msg =>
            msg.id === aiId
              ? { ...msg, message: streamedText, formattedMessage: streamedText }
              : msg
          ));
        },
        { processTier: s.processTier, aiResponseLanguage: aiLangForPrompt },
      );

      if (s.autoCopyResponse && finalText) {
        try { await navigator.clipboard.writeText(finalText); } catch { /* clipboard unavailable */ }
      }
    } catch (error) {
      console.error('Screenshot AI response failed:', error);
      setConversation(prev => prev.map(msg =>
        msg.id === aiId
          ? {
              ...msg,
              message: "I couldn't read that screenshot — try capturing again.",
              formattedMessage: "I couldn't read that screenshot — try capturing again.",
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  // Capture entry point. In Electron, ask the main process to grab the
  // whole screen natively (no screen-share needed — works over Zoom /
  // Teams desktop). On the web, fall back to grabbing a frame from the
  // active screen-share stream.
  const handleScreenshotQuestion = () => {
    if (isLoading || !interviewStarted) return;

    const ipc = typeof window !== 'undefined' && window.electron && window.electron.ipcRenderer;
    if (ipc && typeof ipc.send === 'function') {
      // Electron → native capture; the result comes back over the
      // 'terview:screenshot-captured' IPC channel handled below.
      ipc.send('terview:request-screenshot');
      return;
    }

    // Web path — capture a frame from the shared screen video element.
    if (!isScreenSharing || !screenShareRef.current || !screenShareRef.current.srcObject) {
      alert('Share your screen first so the screenshot can capture what the interviewer is showing.');
      return;
    }
    const video = screenShareRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      alert('Screen share isn\'t ready yet — try again in a moment.');
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      sendScreenshotToAI(canvas.toDataURL('image/jpeg', 0.85));
    } catch (err) {
      console.error('Web screenshot capture failed:', err);
      alert('Couldn\'t capture the screen. Try sharing again.');
    }
  };

  // Keep the refs pointing at the latest handlers so the capture effect
  // (declared above the early returns to satisfy Rules of Hooks) always
  // calls the current closures.
  screenshotHandlerRef.current = handleScreenshotQuestion;
  sendScreenshotToAIRef.current = sendScreenshotToAI;

  const startAutoRecording = async () => {
    const isValidInterviewQuestion = (transcript) => {
      const text = transcript.toLowerCase().trim();
      
      if (text.length < 15) {
        console.log('🚫 Too short (likely noise):', text);
        return false;
      }
      
      const noisePatterns = [
        /^[\s\.\,\!\?\-]+$/,
        /^[a-z]\s*$/i,
        /^(uh+|um+|hmm+|mhm+|uh huh|mm hmm)[\s\.\,]*$/i,
        /^\[.*\]$/,
        /^music$/i,
        /^silence$/i,
        /^\*.*\*$/,
      ];
      
      if (noisePatterns.some(pattern => pattern.test(text))) {
        console.log('🚫 Noise pattern detected:', text);
        return false;
      }
      
      const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
      if (wordCount < 5) {
        console.log('🚫 Too few words (likely noise):', text, '- Word count:', wordCount);
        return false;
      }
      
      console.log('✅ Valid transcript detected:', text);
      return true;
    };

    try {
      setAutoRecordingError('');
      console.log('🎤 Starting auto recording...');

      let stream;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Mac → BlackHole, Windows → VB-Cable Output (the loopback capture device)
        const loopbackDevice = devices.find(device => {
          if (device.kind !== 'audioinput') return false;
          const label = device.label.toLowerCase();
          if (isWindows) {
            return label.includes('cable output') || label.includes('vb-audio') || label.includes('voicemeeter');
          }
          return label.includes('blackhole');
        });

        if (loopbackDevice) {
          console.log(`✅ Found ${loopbackDeviceName} device:`, loopbackDevice.label);
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: loopbackDevice.deviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          });
        } else {
          console.warn(`⚠️ ${loopbackDeviceName} not found, using default audio input`);
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          });
        }
      } catch (err) {
        console.error('Error getting specific device, falling back to default:', err);
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
      }

      autoStreamRef.current = stream;
      setIsAutoRecording(true);
      console.log('✅ Audio stream acquired');

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      let audioChunks = [];
      let isRecording = false;
      let silenceTimer = null;
      let recordingTimer = null;
      let lastSoundTime = Date.now();

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      autoAudioContextRef.current = audioContext;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const VOLUME_THRESHOLD = 20;
      const SILENCE_DURATION = 1000;
      const MAX_RECORDING_DURATION = 45000;
      const MIN_RECORDING_DURATION = 500;

      const checkAudioLevel = () => {
        if (!autoStreamRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;

        if (average > VOLUME_THRESHOLD) {
          lastSoundTime = Date.now();
          
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }

          if (!isRecording && mediaRecorder.state === 'inactive' && !isProcessingRef.current) {
            console.log('🗣️ Speech detected, starting recording...');
            audioChunks = [];
            mediaRecorder.start();
            isRecording = true;

            recordingTimer = setTimeout(() => {
              if (isRecording && mediaRecorder.state === 'recording') {
                console.log('⏱️ Max recording duration reached');
                mediaRecorder.stop();
              }
            }, MAX_RECORDING_DURATION);
          } else if (isProcessingRef.current) {
            console.log('⏸️ Skipping recording - still processing previous question');
          }
        } 
        else if (isRecording && !silenceTimer) {
          const recordingDuration = Date.now() - (lastSoundTime - SILENCE_DURATION);
          
          if (recordingDuration >= MIN_RECORDING_DURATION) {
            silenceTimer = setTimeout(() => {
              if (isRecording && mediaRecorder.state === 'recording') {
                console.log('🤫 Silence detected, stopping recording...');
                mediaRecorder.stop();
              }
            }, SILENCE_DURATION);
          }
        }

        if (autoStreamRef.current) {
          requestAnimationFrame(checkAudioLevel);
        }
      };

      checkAudioLevel();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      }; 

      mediaRecorder.onstop = async () => {
        isRecording = false;
        
        if (silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = null;
        }
        if (recordingTimer) {
          clearTimeout(recordingTimer);
          recordingTimer = null;
        }

        if (audioChunks.length > 0) {
          if (isProcessingRef.current) {
            console.log('⚠️ Already processing, skipping this audio chunk');
            audioChunks = [];
            return;
          }

          console.log('📦 Processing audio chunks...');
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          
          isProcessingRef.current = true;
          
          try {
            const transcript = await transcribeAudioWithWhisper(audioBlob);
            if (transcript && transcript.trim()) {
              console.log('✅ Transcribed:', transcript);
              
              if (isValidInterviewQuestion(transcript)) {
                await handleAudioTranscript(transcript);
              } else {
                console.log('⚠️ Filtered out invalid/noise transcript:', transcript);
              }
            }
          } catch (error) {
            console.error('❌ Transcription error:', error);
            setAutoRecordingError('Failed to transcribe audio. Please try again.');
          } finally {
            isProcessingRef.current = false;
          }
        }

        audioChunks = [];
      };

      autoRecognitionRef.current = { mediaRecorder, stream };
      console.log('✅ Auto recording started successfully');

    } catch (error) {
      console.error('❌ Error starting auto recording:', error);
      setAutoRecordingError(`Failed to start auto recording. Make sure microphone permissions are granted and ${loopbackDeviceName} is configured.`);
      setIsAutoRecording(false);
    }
  };

  const transcribeAudioWithWhisper = async (audioBlob) => {
    try {
      console.log('🔄 Sending audio to Whisper API...');
      console.log('Audio blob size:', audioBlob.size, 'bytes');
      console.log('Audio blob type:', audioBlob.type);
      
      const formData = new FormData();
      
      const file = new File([audioBlob], 'audio.webm', {
        type: 'audio/webm',
        lastModified: Date.now()
      });

      formData.append('audio', file);
      // Hint Whisper with the interviewer's language so transcription
      // accuracy stays high in non-English sessions. 'auto' is dropped
      // on the server side so Whisper auto-detects.
      formData.append('language', settingsRef.current.interviewLanguage || 'auto');

      console.log('📤 Sending file to server:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Transcription failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Transcription response:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Transcription failed');
      }

      return data.transcript;
    } catch (error) {
      console.error('❌ Whisper transcription error:', error);
      throw error;
    }
  };

  const handleModeToggle = async () => {
    const newMode = !isAutoMode;
    
    if (newMode) {
      setAutoRecordingError('');
      
      const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');
      
      if (isElectron) {
        const proceed = window.confirm(
          'Auto Mode uses Web Speech Recognition API which requires:\n\n' +
          '1. Active internet connection\n' +
          '2. Microphone permissions\n' +
          `3. ${loopbackDeviceName} configured as audio input\n\n` +
          'Note: Speech Recognition may not work reliably in Electron. ' +
          'Consider using Manual mode if you experience issues.\n\n' +
          'Do you want to continue with Auto Mode?'
        );
        
        if (!proceed) {
          return;
        }
      }
    }
    
    setIsAutoMode(newMode);

    if (newMode && interviewStarted) {
      await startAutoRecording();
    } else {
      stopAutoRecording();
    }
  };

  const saveInterviewSession = async (isAutoEnd = false) => {
    try {
      const duration = sessionDuration - timeRemaining;
      
      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobRole: formData.jobRole,
          company: formData.company,
          experienceLevel: formData.experienceLevel,
          jobDescription: formData.jobDescription,
          resumeText: formData.resumeText,
          conversation: conversation,
          duration: duration,
          completedNaturally: isAutoEnd
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save interview session');
      }

      const data = await response.json();
      setInterviewSessionId(data.sessionId);
      return data.sessionId;
    } catch (error) {
      console.error('Error saving interview session:', error);
      alert('Failed to save interview session');
    }
  };

  const handleEndInterview = async (isAutoEnd = false) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    stopAutoRecording();
    // Release the screen-capture MediaStream so Chrome's "Sharing this
    // tab" notification bar disappears the moment the session ends.
    // Without this the stream stays alive in the background and the bar
    // sticks around until the user clicks Stop Sharing manually.
    stopScreenShare();

    if (conversation.length > 0) {
      try {
        await saveInterviewSession(isAutoEnd);
        console.log('✅ Interview session saved successfully');
      } catch (error) {
        console.error('❌ Failed to save interview session:', error);
        alert('Failed to save interview session. Please try again.');
        return; // Don't navigate if save failed
      }
    }

    if (isAutoEnd) {
      alert('Time is up! Your interview session has ended and been saved.');
    }

    // Reset interview state before navigating
    setInterviewStarted(false);
    // If the user was sent here from a landing page (via the embedded
    // studio's redirect), drop them back where they came from. Otherwise
    // fall through to the Interview Assistant landing — the dashboard
    // isn't the natural follow-up for "I just finished an interview,"
    // and lets them immediately start another session from the same UI.
    const returnTo = location.state?.returnTo;
    navigate(returnTo || '/InterviewAssistant');
  };

  const handleDownloadTranscript = () => {
    if (conversation.length === 0) {
      alert('No conversation to download yet');
      return;
    }

    const duration = sessionDuration - timeRemaining;
    const durationMinutes = Math.floor(duration / 60);
    const durationSeconds = duration % 60;
    const durationLabel = `${durationMinutes}:${String(durationSeconds).padStart(2, '0')}`;
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const expLabel = formData.experienceLevel
      ? formData.experienceLevel.charAt(0).toUpperCase() + formData.experienceLevel.slice(1) + ' Level'
      : '';
    const statusLabel = timeRemaining <= 0 ? 'Time Ended' : 'Manually Ended';

    const doc = createDoc();
    const { W, H, M, CW } = PDF;
    let y = 0;

    const addNewPage = () => {
      doc.addPage();
      fR(doc, 255, 255, 255, 0, 0, W, H);
      drawPageHeader(doc, formData.jobRole || 'Interview');
      y = 21;
    };
    const checkBreak = (need) => { if (y + need > H - 16) addNewPage(); };

    // Page 1 white bg + cover header
    fR(doc, 255, 255, 255, 0, 0, W, H);
    drawCoverHeader(doc, {
      reportTitle: 'Interview Practice Session',
      role: formData.jobRole,
      company: formData.company,
      levelLabel: expLabel,
      dateStr,
    });
    y = 62;

    // Metadata card
    y = drawMetaCard(doc, [
      { label: 'Position', value: formData.jobRole },
      { label: 'Company', value: formData.company },
      { label: 'Experience Level', value: expLabel },
      { label: 'Date', value: dateStr },
      { label: 'Duration', value: durationLabel },
      { label: 'Total Exchanges', value: Math.floor(conversation.length / 2) },
      { label: 'Status', value: statusLabel },
    ], y) + 12;

    // Section heading
    y = drawSectionHeading(doc, 'Conversation Transcript', y);

    // Messages — chronological order, question then answer per exchange
    const reversed = [...conversation].reverse();
    const ordered = [];
    for (let i = 0; i < reversed.length; i += 2) {
      const a = reversed[i];
      const b = reversed[i + 1];
      if (a && b && a.sender === 'ai' && b.sender === 'interviewer') {
        ordered.push(b); // question first
        ordered.push(a); // answer second
      } else {
        if (a) ordered.push(a);
        if (b) ordered.push(b);
      }
    }
    ordered.forEach((item) => {
      const isUser = item.sender === 'ai';
      const label = isUser ? 'Your Response' : 'Interviewer Question';
      const msgLines = doc.splitTextToSize(item.message || '', CW - 14);
      const cardH = msgLines.length * 5 + 16;
      checkBreak(cardH + 6);

      // Card background
      if (isUser) {
        fR(doc, 246, 244, 255, M, y, CW, cardH);   // light purple
        sR(doc, 196, 181, 253, 0.3, M, y, CW, cardH);
        fR(doc, 109, 40, 217, M, y, 3.5, cardH);   // purple left bar
      } else {
        fR(doc, 255, 245, 250, M, y, CW, cardH);   // light pink
        sR(doc, 255, 182, 214, 0.3, M, y, CW, cardH);
        fR(doc, 219, 39, 119, M, y, 3.5, cardH);   // pink left bar
      }

      // Header row: label + timestamp
      T(doc, isUser ? [76, 29, 149] : [157, 23, 77], 7.5, true, label, M + 7, y + 6.5);
      if (item.timestamp) {
        T(doc, [150, 140, 165], 7, false, item.timestamp, M + CW - 3, y + 6.5, { align: 'right' });
      }

      // Message text
      T(doc, [35, 28, 55], 8, false, msgLines, M + 7, y + 12.5);
      y += cardH + 5;
    });

    drawFooters(doc);

    const filename = `terview-transcript-${(formData.jobRole || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const handleMicClick = async () => {
    if (isLoading || !interviewStarted || isAutoMode) return;

    if (!micActive) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
        });
        micChunksRef.current = [];
        micRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        micRecorderRef.current.ondataavailable = (e) => {
          if (e.data.size > 0) micChunksRef.current.push(e.data);
        };
        micRecorderRef.current.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          if (!micChunksRef.current.length) return;
          const audioBlob = new Blob(micChunksRef.current, { type: 'audio/webm' });
          micChunksRef.current = [];
          // Skip blobs too small to contain real audio (mic tap with no
          // utterance) — Whisper rejects these with an opaque format error.
          if (audioBlob.size < 1024) {
            alert('No audio detected — try holding the mic for at least a second while speaking.');
            return;
          }
          try {
            const transcript = await transcribeAudioWithWhisper(audioBlob);
            if (transcript?.trim()) await handleAudioTranscript(transcript);
          } catch (e) {
            // Surface the real reason so we can diagnose why Whisper
            // is rejecting the upload — generic "try again" was hiding
            // server errors (auth, OpenAI key, format, etc).
            console.error('Mic transcription error:', e);
            const detail = e?.message ? e.message.slice(0, 220) : 'Unknown error';
            alert(`Couldn't transcribe that recording.\n\n${detail}\n\nCheck the browser console for the full server response.`);
          }
        };

        micRecorderRef.current.start(1000);
        setMicActive(true);
      } catch (err) {
        console.error('Mic access error:', err);
        alert('Could not access microphone. Please check permissions.');
      }
    } else {
      if (micRecorderRef.current) {
        micRecorderRef.current.stop();
      }
      setMicActive(false);
    }
  };

  const renderMessageContent = (message, _formattedMessage, sender) => {
    const text = (message ?? '').toString();
    if (sender === 'ai') {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {text}
        </ReactMarkdown>
      );
    }
    return text;
  };

  const handleScreenShare = async () => {
    try {
      setScreenShareError('');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        setScreenShareError('Screen sharing is not supported in your browser');
        return;
      }
      
      // Steer the user toward sharing their MEETING window (Zoom/Meet/Teams)
      // — not the Terview tab. Chrome shows its mandatory "Sharing to this
      // tab" notification inside whichever surface gets captured; by
      // excluding the current tab and biasing the picker toward windows,
      // that bar lands in the meeting app's window instead of here.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' },
        audio: false,
        // Chromium-only hints — silently ignored by other browsers.
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
        systemAudio: 'exclude',
        preferCurrentTab: false,
      });
      
      setIsScreenSharing(true);
      
      stream.getTracks().forEach(track => {
        track.onended = () => {
          setIsScreenSharing(false);
          if (screenShareRef.current) {
            screenShareRef.current.srcObject = null;
          }
        };
      });
      
      if (screenShareRef.current) {
        screenShareRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error sharing screen:', error);
      setScreenShareError('Failed to share screen. Please try again.');
      setIsScreenSharing(false);
    }
  };

  const stopScreenShare = () => {
    if (screenShareRef.current && screenShareRef.current.srcObject) {
      const tracks = screenShareRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      screenShareRef.current.srcObject = null;
    }
    setIsScreenSharing(false);
    setScreenShareError('');
  };
  // Expose stopScreenShare to the unmount-cleanup effect via a ref so
  // the effect (declared above) doesn't TDZ-fail on this `const`.
  stopScreenShareFnRef.current = stopScreenShare;

  return (
    // Theme class only on the standalone route. When `embedded`, this renders
    // inside the /InterviewAssistant landing page and the Electron pane, both
    // of which are dark by design — theming there would bleed into their
    // layouts.
    <div className={`neural-glass-homepage ${themeClass}`}>

      {showJobForm && createPortal(
        <div
          className="job-form-popup-overlay"
          onClick={() => { setShowJobForm(false); setIsEditing(false); }}
          role="presentation"
        >
          <div
            className="job-form-popup glass"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="popup-header">
              <h3>{isEditing ? 'Edit Job Information' : 'Enter Job Information'}</h3>
              <button
                className="close-popup"
                onClick={() => {
                  setShowJobForm(false);
                  setIsEditing(false);
                }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleStartInterview();
            }}>
              <div className="form-group">
                <input
                  type="text"
                  name="jobRole"
                  placeholder="Job Role/Position *"
                  value={formData.jobRole}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              {/* Experience + Company side-by-side to save vertical
                  space in the modal — both fields are short, no reason
                  to give each its own row. */}
              <div className="form-row-2">
                <div className="form-group">
                  <select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Experience Level *</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="company"
                    placeholder="Company (optional)"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group form-group-ip">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#e0a3ff', fontWeight: '500' }}>Job Description *</label>
                  {uploadedJobDescFile && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#9370db', 
                      padding: '6px',
                      background: 'rgba(147, 112, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      📄 {uploadedJobDescFile.name}
                    </div>
                  )}
                  <label htmlFor="job-desc-upload" style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    opacity: isProcessingFile ? 0.6 : 1
                  }}>
                    {isProcessingFile ? 'Processing...' : '📄 Upload File'}
                  </label>
                  <input
                    id="job-desc-upload"
                    type="file"
                    accept=".txt,.doc,.docx,.pdf"
                    onChange={(e) => handleFileUpload(e, 'jobDesc')}
                    disabled={isProcessingFile}
                    style={{ display: 'none' }}
                  />
                </div>
                <textarea
                  name="jobDescription"
                  placeholder="Paste job description or upload a file (PDF, DOCX, DOC, TXT)"
                  value={formData.jobDescription}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={isProcessingFile}
                ></textarea>
              </div>
              
              <div className="form-group form-group-ip">
                <label style={{ color: '#e0a3ff', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Your Resume/CV *</label>
                {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                  <>
                    {/* CSS Grid with equal-width columns + explicit fixed
                        height guarantees both cards are pixel-identical,
                        regardless of <button> vs <label> default sizing
                        quirks. */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px',
                      marginBottom: '12px',
                    }}>
                      <button
                        type="button"
                        /* Colour, fill and border live in CSS (`.ip-resume-choice`)
                           rather than inline: inline styles beat every selector,
                           so a hardcoded `color: white` here left both cards
                           unreadable in light mode with no way to override them.
                           Layout stays inline — only the themeable bits moved. */
                        className={`ip-resume-choice ${resumeChoice === 'profile' ? 'is-active' : ''}`}
                        onClick={() => {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          width: '100%', height: 78, padding: '12px 14px',
                          borderRadius: '8px', cursor: 'pointer',
                          fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxSizing: 'border-box', fontFamily: 'inherit', margin: 0,
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>✅ Use Profile Resume</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {profileData.fullName || 'Use the resume on your profile'}
                        </div>
                      </button>
                      {/* The Upload New Resume card IS the file picker — clicking
                          it sets the choice and opens the OS file dialog in one
                          gesture. No separate "Upload Resume" button needed. */}
                      <label
                        htmlFor="resume-upload"
                        className={`ip-resume-choice ${resumeChoice === 'upload' ? 'is-active' : ''}`}
                        onClick={() => {
                          setResumeChoice('upload');
                          setFormData(prev => ({ ...prev, resumeText: '' }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          width: '100%', height: 78, padding: '12px 14px',
                          borderRadius: '8px',
                          cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                          opacity: isProcessingFile ? 0.6 : 1,
                          fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxSizing: 'border-box', margin: 0,
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>
                          {isProcessingFile ? '⏳ Processing…' : '📄 Upload New Resume'}
                        </div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {uploadedResumeFile ? uploadedResumeFile.name : 'Choose a file (PDF, DOCX, DOC, TXT)'}
                        </div>
                      </label>
                      <input
                        id="resume-upload"
                        type="file"
                        accept=".txt,.doc,.docx,.pdf"
                        onChange={(e) => handleFileUpload(e, 'resume')}
                        disabled={isProcessingFile}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {resumeChoice === 'profile' && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Your profile resume will be used for this interview session.
                      </div>
                    )}
                    {resumeChoice === 'upload' && formData.resumeText && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Resume uploaded — ready to start your interview.
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback: user has no profile resume yet — clicking the
                  // big upload card is the only path, no paste textarea.
                  <>
                    <label htmlFor="resume-upload" style={{
                      display: 'block', padding: '14px 16px', borderRadius: '8px',
                      cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                      opacity: isProcessingFile ? 0.6 : 1,
                      border: `2px solid ${uploadedResumeFile ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                      background: uploadedResumeFile ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                      color: 'white', fontSize: '0.9rem', transition: 'all 0.2s', textAlign: 'left',
                    }}>
                      <div style={{ fontWeight: '600' }}>
                        {isProcessingFile ? '⏳ Processing…' : uploadedResumeFile ? '✓ Resume uploaded' : '📄 Upload your resume'}
                      </div>
                      <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: '3px' }}>
                        {uploadedResumeFile ? uploadedResumeFile.name : 'Choose a file (PDF, DOCX, DOC, TXT)'}
                      </div>
                    </label>
                    <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                      onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                  </>
                )}
              </div>
              
              {fileUploadError && (
                <div style={{
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: fileUploadError.startsWith('✓') 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                  color: fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336',
                  border: `1px solid ${fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336'}`
                }}>
                  {fileUploadError}
                </div>
              )}
              
              <button type="submit" className="cta-button" disabled={isProcessingFile}>
                {isEditing ? 'Update Information' : 'Start Interview (1 Credit)'}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showExitConfirm && createPortal(
        <div
          className="ip-settings-overlay"
          onClick={() => { setShowExitConfirm(false); setPendingExit(null); }}
          role="presentation"
        >
          <div
            className="ip-exit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ip-exit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ip-exit-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 id="ip-exit-title" className="ip-exit-title">End the interview?</h3>
            <p className="ip-exit-body">
              Leaving this page will end your live session. The conversation
              so far will be saved to your dashboard.
            </p>
            <div className="ip-exit-actions">
              <button
                type="button"
                className="ip-exit-btn ip-exit-cancel"
                onClick={() => { setShowExitConfirm(false); setPendingExit(null); }}
              >
                Stay
              </button>
              <button
                type="button"
                className="ip-exit-btn ip-exit-confirm"
                onClick={async () => {
                  setShowExitConfirm(false);
                  // Tear down the live session: stop timer + recording, save
                  // the transcript to the dashboard. Mirrors handleEndInterview
                  // but without its built-in /dashboard navigate so we can
                  // honor the user's actual chosen destination instead.
                  if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
                  stopAutoRecording();
                  // Release the screen-capture stream so Chrome's
                  // "Sharing this tab" bar goes away on exit, not just
                  // when the user clicks the End Session button.
                  stopScreenShare();
                  if (conversation.length > 0) {
                    try { await saveInterviewSession(false); }
                    catch (err) { console.error('Failed to save interview session on exit:', err); }
                  }
                  setInterviewStarted(false);

                  if (pendingExit) {
                    // In-app navigation (launchpad / header / brand). Run the
                    // caller's navigate fn so router state stays consistent.
                    const { path, navigateFn, resolve } = pendingExit;
                    setPendingExit(null);
                    navigateFn(path);
                    resolve?.();
                  } else {
                    // Browser back/forward triggered the modal — no caller
                    // navigate available, so fall back to the Interview
                    // Assistant landing (or the explicit returnTo from
                    // the launching landing page).
                    const returnTo = location.state?.returnTo;
                    navigate(returnTo || '/InterviewAssistant');
                  }
                }}
              >
                Yes, end session
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showSettings && createPortal(
        <div
          className="ip-settings-overlay"
          onClick={() => setShowSettings(false)}
          role="presentation"
        >
          <div
            className="ip-settings-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="ip-settings-head">
              <h3>Settings</h3>
              <button
                type="button"
                className="ip-settings-close"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="ip-settings-body">
              <IPField label="Response length" hint="How long the AI's spoken answer should be.">
                <IPSegmented
                  value={draftSettings.responseLength}
                  onChange={(v) => setDraftSettings(s => ({ ...s, responseLength: v }))}
                  options={[
                    { value: 'concise',  label: 'Concise' },
                    { value: 'default',  label: 'Default' },
                    { value: 'detailed', label: 'Detailed' },
                  ]}
                />
              </IPField>

              <IPField label="Interview language" hint="Language the interviewer is speaking. Improves transcription accuracy.">
                <select
                  className="ip-select"
                  value={draftSettings.interviewLanguage}
                  onChange={e => setDraftSettings(s => ({ ...s, interviewLanguage: e.target.value }))}
                >
                  {IP_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </IPField>

              <IPField label="AI response language" hint="Auto matches the interviewer; pick a language to force a specific reply.">
                <select
                  className="ip-select"
                  value={draftSettings.aiResponseLanguage}
                  onChange={e => setDraftSettings(s => ({ ...s, aiResponseLanguage: e.target.value }))}
                >
                  {IP_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </IPField>

              <IPField label="Process time" hint="Quality uses a stronger model — slower but better answers.">
                <IPSegmented
                  value={draftSettings.processTier}
                  onChange={(v) => setDraftSettings(s => ({ ...s, processTier: v }))}
                  options={[
                    { value: 'fast',    label: 'Fast' },
                    { value: 'medium',  label: 'Medium' },
                    { value: 'quality', label: 'Quality' },
                  ]}
                />
              </IPField>

              <IPField label="Auto-skip noise" hint="Drop very short bursts (background noise, throat-clears) before transcribing.">
                <IPSegmented
                  value={draftSettings.autoSkipNoise ? 'on' : 'off'}
                  onChange={(v) => setDraftSettings(s => ({ ...s, autoSkipNoise: v === 'on' }))}
                  options={[
                    { value: 'on',  label: 'Enable' },
                    { value: 'off', label: 'Disable' },
                  ]}
                />
              </IPField>

              <IPField label="Noise threshold" hint="Lower = more sensitive (catches quiet speech). Higher = stricter.">
                <div className="ip-slider-row">
                  <input
                    type="range" min="0.05" max="0.6" step="0.01"
                    value={draftSettings.noiseThreshold}
                    onChange={e => setDraftSettings(s => ({ ...s, noiseThreshold: parseFloat(e.target.value) }))}
                    className="ip-range"
                    disabled={!draftSettings.autoSkipNoise}
                  />
                  <span className="ip-slider-value">{draftSettings.noiseThreshold.toFixed(2)}</span>
                </div>
              </IPField>

              <IPField label="Font size" hint="Size of the AI response text in the conversation.">
                <div className="ip-slider-row">
                  <input
                    type="range" min="12" max="22" step="1"
                    value={draftSettings.fontSize}
                    onChange={e => setDraftSettings(s => ({ ...s, fontSize: parseInt(e.target.value, 10) }))}
                    className="ip-range"
                  />
                  <span className="ip-slider-value">{draftSettings.fontSize}px</span>
                </div>
              </IPField>

              <IPField label="Privacy" hint="Useful when you're screen-sharing during the interview.">
                <div className="ip-toggle-stack">
                  <IPToggleRow
                    label="Hide interviewer question text"
                    checked={draftSettings.hideQuestionText}
                    onChange={(v) => setDraftSettings(s => ({ ...s, hideQuestionText: v }))}
                  />
                  <IPToggleRow
                    label="Auto-copy AI response to clipboard"
                    checked={draftSettings.autoCopyResponse}
                    onChange={(v) => setDraftSettings(s => ({ ...s, autoCopyResponse: v }))}
                  />
                </div>
              </IPField>
            </div>
            <div className="ip-settings-foot">
              <button
                type="button"
                className="ip-settings-reset"
                onClick={() => setDraftSettings(IP_DEFAULT_SETTINGS)}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                className="ip-settings-save"
                onClick={() => {
                  setSettings(draftSettings);
                  try { localStorage.setItem(IP_SETTINGS_KEY, JSON.stringify(draftSettings)); } catch {}
                  setShowSettings(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}
      
      <div className={`interview-page-container ${embedded ? 'interview-embedded' : ''}`}>

        {!embedded && (
          <>
            <Header
              setShowAuthModal={setShowAuthModal}
              onNavigate={handleHeaderNavigation}
              interviewInProgress={interviewStarted}
            />
            <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />
          </>
        )}

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to start an interview session.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className={`interview-page-main ${interviewStarted && !embedded ? 'ip-live-mode' : ''} ${bootingFromPrefill && !embedded ? 'ip-booting' : ''}`}>
          {bootingFromPrefill && !embedded && (
            <div className="ip-boot-splash" role="status" aria-live="polite">
              <div className="ip-boot-spinner" aria-hidden="true" />
              <p className="ip-boot-label">Setting up your interview…</p>
              <p className="ip-boot-sub">Loading your job context and resume.</p>
            </div>
          )}
          {/* Theme switch — sits in the gutter beside the header pill on wide
              screens, and falls back to a right-aligned strip above the
              workspace when the viewport is too narrow for that (see
              .cls-theme-dock in studioTheme.css). Stays in the DOM here
              so the fallback lands in the right place. Hidden only when
              embedded (the landing page and Electron pane are dark by
              design). Deliberately NOT gated on `interviewStarted`: arriving
              here from the landing auto-starts the session, so gating it
              would leave the control visible for barely a second. */}
          <div className="interview-main-container" style={bootingFromPrefill && !embedded ? { display: 'none' } : undefined}>
            <div className="form-section">
              <div className="interview-form-container glass">
                {interviewStarted ? (
                  <>
                    {!isElectronApp && (
                    <div className={`screen-share-section ${isScreenSharing ? '' : 'compact'}`}>
                      {screenShareError && (
                        <div className="screen-share-error">{screenShareError}</div>
                      )}
                      {/* The <video> stays mounted at all times so the ref is
                          available the moment handleScreenShare assigns
                          srcObject. We hide the preview frame with CSS when
                          not sharing — that's what `compact` does. */}
                      {/* Preview frame — always visible so the user knows
                          where the shared screen will appear. When not
                          sharing, shows a friendly placeholder; when
                          sharing kicks on, the placeholder hides and the
                          <video> takes over inside the same frame. */}
                      <div className="screen-share-preview">
                        <video
                          ref={screenShareRef}
                          autoPlay
                          muted
                          playsInline
                          style={{
                            width: '100%',
                            display: isScreenSharing ? 'block' : 'none',
                            border: 'none',
                            borderRadius: 0,
                          }}
                        />
                        {!isScreenSharing && (
                          <div className="screen-share-placeholder">
                            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="2" y="3" width="20" height="14" rx="2" />
                              <line x1="8" y1="21" x2="16" y2="21" />
                              <line x1="12" y1="17" x2="12" y2="21" />
                            </svg>
                            <div className="screen-share-placeholder-title">Your shared screen will appear here</div>
                            <div className="screen-share-placeholder-sub">Click <strong>Share Screen</strong> below, then pick your <strong>meeting window</strong> (Zoom, Meet, Teams) — not this tab.</div>
                          </div>
                        )}
                      </div>
                      <div className="screen-share-controls">
                        {!isScreenSharing ? (
                          <button
                            onClick={handleScreenShare}
                            className="screen-share-btn"
                            title="Share your screen with the interviewer"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="2" y="3" width="20" height="14" rx="2" />
                              <line x1="8" y1="21" x2="16" y2="21" />
                              <line x1="12" y1="17" x2="12" y2="21" />
                            </svg>
                            <span>Share Screen</span>
                          </button>
                        ) : (
                          <button
                            onClick={stopScreenShare}
                            className="stop-share-btn"
                            title="Stop sharing your screen"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <rect x="6" y="6" width="12" height="12" rx="1.5" />
                            </svg>
                            <span>Stop Sharing</span>
                          </button>
                        )}
                      </div>
                    </div>
                    )}

                  </>
                ) : (
                  <>
                    <p className="form-subtitle">Enter your details to customize the interview experience</p>

                    <button
                      onClick={() => {
                        const hasProfileResume = profileData && (
                          profileData.professionalSummary ||
                          profileData.skills ||
                          (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))
                        );
                        if (hasProfileResume && !resumeChoice) {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                        } else if (!hasProfileResume && !resumeChoice) {
                          setResumeChoice('upload');
                        }
                        setShowJobForm(true);
                      }}
                      className="start-interview-btn"
                    >
                      Enter Job Information
                    </button>
                  </>
                )}
              </div>
            </div>

            {interviewStarted && <div className="interview-section active">
              <div className="interview-container">
                <div className="interview-header">
                  <h2 className="ip-chat-title">
                    <span className="ip-chat-title-text">
                      Interview for: {formData?.jobRole || 'Your Position'}
                    </span>
                    <button
                      type="button"
                      className="ip-edit-job-btn"
                      onClick={handleEditJobInfo}
                      title="Edit job information"
                      aria-label="Edit job information"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                  </h2>
                  <div className="ip-toolbar">
                    {interviewStarted && (
                      <>
                        {isElectronApp && (
                          <div className="ip-tool-group ip-mode-toggle" role="group" aria-label="Recording mode">
                            <button
                              type="button"
                              className={`ip-mode-opt${!isAutoMode ? ' is-active' : ''}`}
                              onClick={() => { if (isAutoMode) handleModeToggle(); }}
                              aria-pressed={!isAutoMode}
                            >
                              Manual
                            </button>
                            <button
                              type="button"
                              className={`ip-mode-opt${isAutoMode ? ' is-active' : ''}`}
                              onClick={() => { if (!isAutoMode) handleModeToggle(); }}
                              aria-pressed={isAutoMode}
                            >
                              Auto
                            </button>
                          </div>
                        )}

                        {isElectronApp && isAutoMode && (
                          <div className={`ip-status-chip${isAutoRecording ? ' is-live' : ''}`}>
                            <span className="ip-status-dot" />
                            {isAutoRecording ? 'Listening' : 'Waiting'}
                          </div>
                        )}

                        <div className="ip-tool-pill ip-font-stepper" title={`Text size: ${fontSize}px`}>
                          <span className="ip-font-glyph sm" aria-hidden="true">A</span>
                          <input
                            type="range" min="11" max="22" value={fontSize}
                            onChange={e => setFontSize(Number(e.target.value))}
                            aria-label="Adjust text size"
                          />
                          <span className="ip-font-glyph lg" aria-hidden="true">A</span>
                        </div>

                        <button
                          type="button"
                          onClick={handleMicClick}
                          disabled={isLoading || isAutoMode}
                          title={micActive ? 'Stop recording' : 'Record interviewer question'}
                          aria-label={micActive ? 'Stop recording' : 'Record interviewer question'}
                          aria-pressed={micActive}
                          className={`ip-tool-btn ip-mic-toggle${micActive ? ' is-on' : ''}`}
                        >
                          {micActive ? (
                            // Stop square — same icon as the composer mic
                            // when recording, so users learn one symbol
                            // for "tap to stop and transcribe."
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <rect x="6" y="6" width="12" height="12" rx="1" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                              <line x1="12" y1="19" x2="12" y2="23" />
                              <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                          )}
                        </button>

                        <div className={`ip-tool-pill ip-timer${timeRemaining < 300 ? ' is-low' : ''}`} aria-label="Time remaining">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span className="ip-timer-val">{formatTime(timeRemaining)}</span>
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => { setDraftSettings(settings); setShowSettings(true); }}
                      className="ip-tool-btn ip-settings-trigger"
                      title="Settings"
                      aria-label="Open settings"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </button>
                    <button type="button" onClick={() => handleEndInterview(false)} className="end-button">
                      End Session
                    </button>
                  </div>
                </div>
                
                <div className="conversation-container" ref={conversationContainerRef}>
                  {autoRecordingError && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(255, 0, 0, 0.1)',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                      borderRadius: '8px',
                      color: '#ff6b6b',
                      marginBottom: '10px'
                    }}>
                      {autoRecordingError}
                    </div>
                  )}

                  {!interviewStarted ? (
                    <div className="welcome-message">
                      <h3>Complete the form to begin your interview</h3>
                      <p>Click "Enter Job Information" on the left side to begin.</p>
                      <p>Once started, I'll help you with both technical and behavioral interview questions.</p>
                    </div>
                  ) : conversation.length === 0 ? (
                    <div className="welcome-message">
                      <h3>Ready to begin your interview!</h3>
                      {isAutoMode ? (
                        <>
                          <p>Auto mode is active. The system will automatically detect and transcribe interviewer questions.</p>
                          <p>Make sure {loopbackDeviceName} is configured as your audio input device to capture system audio.</p>
                          <p>When the interviewer speaks, their question will be automatically transcribed and answered.</p>
                        </>
                      ) : (
                        <>
                          <p>When the interviewer asks a question, click "Record Interviewer Question" below.</p>
                          <p>I'll generate appropriate responses based on your resume and the job requirements.</p>
                        </>
                      )}
                      <p>You have 30 minutes for this session.</p>
                    </div>
                  ) : (
                    (() => {
                      // The MOST RECENT interviewer question is the first
                      // 'interviewer'-sender item in the conversation array
                      // (newest first, since each round is prepended). We attach
                      // a ref to it so the scroll effect can pin it to the top
                      // of the container — the streaming AI answer below it
                      // grows downward without ever pushing the question out of
                      // view.
                      const latestQ = conversation.find(m => m.sender === 'interviewer');
                      // Render newest-first: the conversation array is
                      // already in [Q_n, A_n, Q_n-1, A_n-1, …] order
                      // (each new round is prepended Q-then-A), so a
                      // straight iteration in column flow puts the
                      // most recent exchange at the visual TOP.
                      const ordered = conversation;
                      const QUICK_PROMPTS = [
                        { label: 'Shorter',        mod: 'Rewrite the previous answer in 3 short sentences max.' },
                        { label: 'More technical', mod: 'Make the previous answer more technical with concrete tools, metrics, and architecture.' },
                        { label: 'Add example',    mod: 'Add a specific real-world example with a company, project, or measurable outcome.' },
                        { label: 'Use STAR',       mod: 'Restructure the answer using the STAR method (Situation, Task, Action, Result) as flowing prose.' },
                      ];
                      return ordered.map((item, idx) => {
                        // Walk BACKWARDS to find the question paired with this
                        // AI answer. setConversation now prepends [ai, question,
                        // ...prev] so after reversal the order is
                        // [Q_1, A_1, ..., Q_n, A_n] — the Q sits at idx-1.
                        let pairedQ = null;
                        if (item.sender === 'ai') {
                          for (let k = idx - 1; k >= 0; k--) {
                            if (ordered[k].sender === 'interviewer') {
                              pairedQ = ordered[k];
                              break;
                            }
                          }
                        }
                        return (
                        <div
                          key={item.id}
                          ref={latestQ && item.id === latestQ.id ? latestQuestionRef : null}
                          className={`message ${item.sender}`}
                        >
                          <div className="message-header">
                            <strong>
                              {item.sender === 'ai'
                                ? 'Suggested Response'
                                : item.kind === 'screenshot'
                                  ? '📸 Screen Capture'
                                  : 'Interviewer Question'}
                            </strong>
                            <span className="timestamp">{item.timestamp}</span>
                          </div>
                          {item.sender === 'interviewer' && item.image && (
                            <a
                              href={item.image}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="message-screenshot"
                              title="Open full size in new tab"
                            >
                              <img src={item.image} alt="Captured screen content" />
                            </a>
                          )}
                          <div className="message-content" style={{ fontSize: `${fontSize}px`, lineHeight: 1.65 }}>
                            {item.sender === 'interviewer' && settings.hideQuestionText
                              ? <em style={{ opacity: 0.6 }}>Question hidden — privacy mode on</em>
                              : item.sender === 'interviewer' && item.kind === 'screenshot' && !item.message
                                ? <em style={{ opacity: 0.6 }}>The interviewer shared this on screen — see the AI's analysis below.</em>
                                : renderMessageContent(item.message, item.formattedMessage, item.sender)}
                          </div>
                          {item.sender === 'ai' && item.message && !item.streaming && (
                            <div className="message-actions">
                              <button
                                type="button"
                                className="msg-action-btn"
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(item.message);
                                    setCopiedId(item.id);
                                    setTimeout(() => setCopiedId(null), 1500);
                                  } catch { /* clipboard blocked */ }
                                }}
                                title="Copy answer"
                              >
                                {copiedId === item.id ? (
                                  <>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                    Copy
                                  </>
                                )}
                              </button>
                              {pairedQ && QUICK_PROMPTS.map(qp => (
                                <button
                                  key={qp.label}
                                  type="button"
                                  className="msg-action-btn ip-quick-chip-inline"
                                  onClick={() => generateAIResponse(`${pairedQ.message}\n\n[Follow-up instruction: ${qp.mod}]`)}
                                  disabled={isLoading}
                                  title={`Regenerate this answer — ${qp.label}`}
                                >
                                  {qp.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                      });
                    })()
                  )}
                </div>
                
                
                {isElectronApp && isAutoMode && interviewStarted && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: 'rgba(147, 112, 219, 0.1)',
                    border: '1px solid rgba(147, 112, 219, 0.3)',
                    borderRadius: '8px',
                    color: '#e0a3ff',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                  }}>
                    <strong>Auto Mode Active:</strong> The system is listening for interviewer questions via {loopbackDeviceName} audio routing.
                    Questions will be automatically transcribed and answered.
                  </div>
                )}

                {/* Composer — paste/type a question or hit the mic. Mirrors
                    the Electron pane so the surfaces feel the same. */}
                <div className="ip-composer">
                  <textarea
                    className="ip-composer-input"
                    placeholder={micActive
                      ? 'Recording… click the mic to stop and transcribe'
                      : "Paste/type the interviewer's question, or click the mic to record it."}
                    value={questionInput}
                    onChange={e => setQuestionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && questionInput.trim() && !isLoading && !micActive) {
                        e.preventDefault();
                        const q = questionInput.trim();
                        setQuestionInput('');
                        generateAIResponse(q);
                      }
                    }}
                    rows={1}
                    disabled={isLoading || micActive}
                  />
                  <button
                    type="button"
                    className="ip-composer-snap"
                    onClick={handleScreenshotQuestion}
                    /* Electron captures the screen natively — no share needed.
                       Web needs an active screen-share to grab a frame. */
                    disabled={isLoading || (!isElectronApp && !isScreenSharing)}
                    title={isElectronApp
                      ? 'Capture your screen and ask the AI about it (⌘/Ctrl + Shift + S)'
                      : isScreenSharing
                        ? 'Capture the shared screen and ask the AI about it (⌘/Ctrl + Shift + S)'
                        : 'Share your screen first to use screenshot capture'}
                    aria-label="Capture screenshot and ask AI"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`ip-composer-mic ${micActive ? 'recording' : ''}`}
                    onClick={handleMicClick}
                    disabled={isLoading}
                    title={micActive ? 'Stop & transcribe' : 'Record interviewer question'}
                    aria-label={micActive ? 'Stop recording' : 'Start recording'}
                  >
                    {micActive ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <rect x="6" y="6" width="12" height="12" rx="1" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="ip-composer-send"
                    onClick={() => {
                      const q = questionInput.trim();
                      if (!q) return;
                      setQuestionInput('');
                      generateAIResponse(q);
                    }}
                    disabled={isLoading || micActive || !questionInput.trim()}
                  >
                    {isLoading ? 'Thinking…' : 'Get answer'}
                  </button>
                </div>
              </div>
            </div>}
          </div>

        </main>
      </div>
    </div>
  );
};

export default InterviewPage;
